import { supabase } from '../supabase/client';
import { API_CONFIG, getApiUrl } from '../config';

/**
 * Get the user's billing status from the API
 * @returns {Promise<{billingAcctSetup: boolean, currentValidSubscription: boolean, cachedCheck: boolean}>}
 */
export const getBillingStatus = async () => {
  try {
    // Get the user's auth token
    const { data: authData } = await supabase.auth.getSession();
    const token = authData?.session?.access_token;
    
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    // Call the API endpoint
    const response = await fetch(
      getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.GET_BILLING_STATUS),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get billing status: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting billing status:', error);
    // Return default values if there's an error
    return {
      billingAcctSetup: false,
      currentValidSubscription: false,
      cachedCheck: false
    };
  }
};

/**
 * Get the URL for managing an existing subscription
 * @returns {Promise<{redirectUrl: string}>}
 */
export const getManageAccountUrl = async () => {
  try {
    // Get the user's auth token
    const { data: authData } = await supabase.auth.getSession();
    const token = authData?.session?.access_token;
    
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    // Call the API endpoint
    const response = await fetch(
      getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.GET_MANAGE_ACCOUNT_URL),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get manage account URL: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting manage account URL:', error);
    // Return empty URL if there's an error
    return { redirectUrl: '' };
  }
};

/**
 * Get the URL for checking out (subscribing)
 * @returns {Promise<{redirectUrl: string}>}
 */
export const getCheckoutUrl = async () => {
  try {
    // Get the user's auth token
    const { data: authData } = await supabase.auth.getSession();
    const token = authData?.session?.access_token;
    
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    // Call the API endpoint
    const response = await fetch(
      getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.GET_CHECKOUT_URL),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get checkout URL: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting checkout URL:', error);
    // Return empty URL if there's an error
    return { redirectUrl: '' };
  }
}; 